import { exists, filterDuplicates, filterInstances, flatArray, sortArray, StaticLogger } from '@nu-art/ts-common';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_OrderGroup, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_PathwayTransition } from '@app/advisor-frontend/modules/static-modules/pathway';
import { ModuleFE_Advisor_Expression } from '@app/advisor-frontend/modules/static-modules/healthcare-space';
import { DBDef_Action, DBDef_OrderGroup } from '@app/pathway-shared';
import { ProtocolData } from '../../_utils/ProtocolData/ProtocolData.js';
import { getExpressionTermVariableIds, mapProvideInfoValueSpace, supplementExpressionVariablesInProvideInfoFindings } from './_utils/finding-mapping.js';
import { EditableItem } from '@nu-art/thunderstorm-frontend';
import { parseExpression_Normalized } from '@app/hcs-shared/_entity/expression/antlr/parser';
import { getNeededParsedExpressionIds } from '@app/hcs-frontend/ui/_components/expression-pretty-print/Component_PrettyPrint_Expression/get-needed-ids';
import { calculate_ExpressionValueTree } from '@app/hcs-frontend/ui/_components/expression-pretty-print/Component_PrettyPrint_Expression/value-tree';
const getExpressionVariableIds = (description, valueTree, expression) => {
    if (!description)
        return [];
    const items = getNeededParsedExpressionIds(description, valueTree, expression);
    //Transform - Resolve each item into a final var id
    let ids = filterInstances(items.map(item => {
        switch (item.type) {
            case 'variable':
                return item.id;
            case 'expression':
                return ModuleFE_Advisor_Expression.cache.unique(item.id)?.associatedVar;
        }
    }));
    //Filter - remove duplications
    ids = filterDuplicates(ids);
    return ids;
};
const getValueMapping = (findings) => {
    const items = [...findings.toCreate, ...findings.toUpdate];
    const map = {};
    const getValue = (item) => {
        switch (item.type) {
            case 'numeric':
                return item.value;
            case 'enumerated':
                return item.valueIds?.[0];
        }
    };
    items.forEach(item => {
        //Assign value
        const value = getValue(item);
        if (value)
            map[item.variableId] = value;
        //Assign attribute values
        if (item.type === 'enumerated' && item.attributes?.length) {
            item.attributes.forEach(att => {
                if (att.valueIds?.[0])
                    map[att.variableId] = att.valueIds?.[0];
            });
        }
        //Handle suggested finding
        if (item.suggestedFinding && !(item.suggestedFinding.explicitReject || item.suggestedFinding.userInteracted)) {
            const suggestedValue = getValue(item.suggestedFinding.finding);
            if (suggestedValue && !map[item.variableId])
                map[item.variableId] = suggestedValue;
            //Handle suggested attributes
            if (item.suggestedFinding.finding.type === 'enumerated' && item.suggestedFinding.finding.attributes?.length)
                item.suggestedFinding.finding.attributes.forEach(att => {
                    if (!map[att.variableId] && att.valueIds?.[0])
                        map[att.variableId] = att.valueIds[0];
                });
        }
    });
    return map;
};
//######################### Expression #########################
export const provideInfo_GetInputScope_Expression = (expression, description, valueTree, findings) => {
    const ids = getExpressionVariableIds(description, valueTree, expression);
    const valueMapping = getValueMapping(findings);
    //Filter - filter out ids if they have actual / suggested values
    return ids.filter(id => !exists(valueMapping[id]));
};
export const getProvideInfoContextData_Expression = (expression, neededVariables, onSubmit, existingFindings) => {
    const emFindings = mapProvideInfoValueSpace(neededVariables, existingFindings);
    let expressionDescription;
    let valueTree;
    try {
        expressionDescription = parseExpression_Normalized(expression.expression);
        valueTree = calculate_ExpressionValueTree(expressionDescription, {});
    }
    catch (err) {
        if (expression.expression !== 'true')
            StaticLogger.logError('getProvideInfoContextData_Expression', err);
    }
    const inputScope = provideInfo_GetInputScope_Expression(expression, expressionDescription, valueTree, emFindings);
    const selectedVarId = inputScope[0];
    return {
        expression,
        expressionDescription,
        neededVariables: neededVariables,
        findings: new EditableItem(emFindings).setSaveAction(onSubmit),
        partialCalculationFindings: [],
        calculationInProgress: false,
        input: { show: exists(selectedVarId), scope: inputScope, varId: selectedVarId },
    };
};
//######################### Score #########################
export const getProvideInfoContextData_Score = (expression, neededVariables, onSubmit, existingFindings) => {
    const emFindings = mapProvideInfoValueSpace(neededVariables, existingFindings);
    let expressionDescription;
    let valueTree;
    try {
        expressionDescription = parseExpression_Normalized(expression.expression);
        valueTree = calculate_ExpressionValueTree(expressionDescription, {});
    }
    catch (err) {
        if (expression.expression !== 'true')
            StaticLogger.logError('getProvideInfoContextData_Expression', err);
    }
    const inputScope = provideInfo_GetInputScope_Expression(expression, expressionDescription, valueTree, emFindings);
    const selectedVarId = inputScope[0];
    return {
        expression,
        expressionDescription,
        neededVariables: neededVariables,
        findings: new EditableItem(emFindings).setSaveAction(onSubmit),
        partialCalculationFindings: [],
        calculationInProgress: false,
        input: { show: exists(selectedVarId), scope: inputScope, varId: selectedVarId },
    };
};
//######################### Protocol #########################
const resolve_OrdersFromPointers = (pointers) => {
    const orders = { orders: [], groups: [] };
    pointers.forEach(pointer => {
        switch (pointer.dbKey) {
            case DBDef_Action.dbKey: {
                const order = ModuleFE_Advisor_Action.cache.unique(pointer.id);
                if (order)
                    orders.orders.push(order);
                break;
            }
            case DBDef_OrderGroup.dbKey: {
                const group = ModuleFE_Advisor_OrderGroup.cache.unique(pointer.id);
                if (group)
                    orders.groups.push(group);
            }
        }
    });
    return orders;
};
const getNextStep_PreConditions = (targetState) => {
    return resolve_OrdersFromPointers(targetState.preconditions.map(precondition => precondition.itemPointer));
};
const getNextStep_TransitionOrders = (transition) => {
    const pointers = [
        ...(transition.testsIds ?? []),
        ...(transition.treatmentsIds ?? []),
        ...(transition.consultIds ?? []),
        ...(transition.nurseIds ?? []),
        ...(transition.additionalOrdersIds ?? []),
    ];
    return resolve_OrdersFromPointers(pointers);
};
const getProtocolNextSteps = (protocolData) => {
    const currentState = protocolData.getActiveState();
    if (!currentState?.stateDbId)
        return [];
    const transitions = ModuleFE_Advisor_PathwayTransition.cache.filter(t => t.sourceId === currentState.stateDbId && t.pathwayIds.includes(protocolData.pathway._id));
    sortArray(transitions, transition => transition.priorityIndex);
    return filterInstances(transitions.map(transition => getProtocolNextStep(transition)));
};
export const getProtocolNextStep = (_transition) => {
    const transition = typeof _transition === 'string' ? ModuleFE_Advisor_PathwayTransition.cache.unique(_transition) : _transition;
    if (!transition)
        return;
    const originState = ModuleFE_Advisor_PathwayState.cache.unique(transition.sourceId);
    if (!originState)
        return;
    const targetState = ModuleFE_Advisor_PathwayState.cache.unique(transition.targetId);
    if (!targetState)
        return;
    const relatedExpression = ModuleFE_Advisor_Expression.cache.unique(transition.resolutionFunctionId);
    if (!relatedExpression)
        return;
    let expressionDescription;
    try {
        expressionDescription = parseExpression_Normalized(relatedExpression.expression);
    }
    catch (err) {
        if (relatedExpression.expression !== 'true')
            StaticLogger.logError('getProtocolNextStep', err);
    }
    return {
        transition,
        originState,
        targetState,
        relatedExpression,
        expressionDescription,
        preConditionOrders: getNextStep_PreConditions(targetState),
        transitionOrders: getNextStep_TransitionOrders(transition),
    };
};
export const getProvideInfoContextData_Protocol = (protocolId, navMode, onSubmit) => {
    const protocolData = ProtocolData.createNew(protocolId);
    if (!protocolData)
        return;
    const emFindings = mapProvideInfoValueSpace(protocolData.protocol.neededVariables);
    const steps = getProtocolNextSteps(protocolData);
    supplementExpressionVariablesInProvideInfoFindings(emFindings, steps.map(step => step.relatedExpression));
    return {
        protocolData,
        navMode,
        nextSteps: steps,
        calculationInProgress: false,
        findings: new EditableItem(emFindings).setSaveAction(onSubmit),
        partialCalculationFindings: [],
        neededVariables: protocolData.protocol.neededVariables,
        openedSteps: {},
        input: { show: false },
    };
};
export const provideInfo_GetStepVariableIds = (steps) => {
    return filterDuplicates(flatArray(steps.map(step => getExpressionVariableIds(step.expressionDescription, step.valueTree, step.relatedExpression))));
};
export const provideInfo_GetAllStepVariableIds = (step) => {
    return getExpressionTermVariableIds(step.relatedExpression);
};
const getFindingScalar = (item) => {
    if (!item)
        return;
    return item.type === 'numeric' ? item.value : item.valueIds?.[0];
};
export const provideInfo_GetQuestionValues = (findings, variableId) => {
    const items = [...findings.toCreate, ...findings.toUpdate];
    const top = items.find(item => item.variableId === variableId);
    if (top) {
        const suggested = top.suggestedFinding;
        return {
            userValue: top.isPartialCalculation ? undefined : getFindingScalar(top),
            inferredValue: suggested && !suggested.explicitReject && !suggested.userInteracted
                ? getFindingScalar(suggested.finding)
                : undefined,
        };
    }
    for (const entry of items) {
        if (entry.type !== 'enumerated')
            continue;
        const attribute = entry.attributes?.find(att => att.variableId === variableId);
        if (!attribute)
            continue;
        const suggested = entry.suggestedFinding;
        const inferredAttribute = suggested?.finding.type === 'enumerated'
            ? suggested.finding.attributes?.find(att => att.variableId === variableId)
            : undefined;
        return {
            userValue: attribute.valueIds?.[0],
            inferredValue: suggested && !suggested.explicitReject && !suggested.userInteracted
                ? inferredAttribute?.valueIds?.[0]
                : undefined,
        };
    }
    return { userValue: undefined, inferredValue: undefined };
};
export const provideInfo_GetInputScope_Protocol = (steps, findings) => {
    const valueMapping = getValueMapping(findings);
    return provideInfo_GetStepVariableIds(steps).filter(id => !exists(valueMapping[id]));
};
//# sourceMappingURL=helpers.js.map